import React, { useEffect, useState } from "react"
import { Button, Checkbox, Input, Modal, notification, Spin, Tag } from "antd"
import {
	CheckCircleFilled,
	ClockCircleOutlined,
	CheckOutlined,
	WarningFilled,
} from "@ant-design/icons"
import Strings from "utils/strings"
import { API, Endpoints } from "utils/api"

interface AggregationBox {
	_id: string
	boxId: string
	boxWeight: number
	temporary: boolean
	registeredId: boolean
	lot: string
	orders: Array<{
		_id: string
		orderId: string
		bagId: string
		treated: boolean
	}>
}

// ─── Shared style constants ───────────────────────────────────────────────────

const cardBase: React.CSSProperties = {
	background: "#fff",
	borderRadius: 10,
	boxShadow: "0 2px 10px rgba(0,0,0,0.07)",
	display: "flex",
	flexDirection: "column",
	overflow: "hidden",
}

// ─── BoxCard ──────────────────────────────────────────────────────────────────

interface BoxCardProps {
	box: AggregationBox
	loading: boolean
	treatedOrders: Record<string, boolean>
	loadingOrders: Record<string, boolean>
	boxIdInput: string
	onBoxIdChange: (val: string) => void
	onBoxIdSubmit: () => void
	onTreatedChange: (orderId: string, checked: boolean) => void
	onFinalize: () => void
	isPendingConfirm: boolean
	onConfirmFinalize: () => void
	onCancelFinalize: () => void
}

const BoxCard: React.FC<BoxCardProps> = ({
	box,
	loading,
	treatedOrders,
	loadingOrders,
	boxIdInput,
	onBoxIdChange,
	onBoxIdSubmit,
	onTreatedChange,
	onFinalize,
	isPendingConfirm,
	onConfirmFinalize,
	onCancelFinalize,
}) => {
	const [hoveredOrder, setHoveredOrder] = React.useState<string | null>(null)
	const isFinalized = !box.temporary
	const accentColor = isFinalized ? "#52c41a" : "#227cb5"
	const headerBg = isFinalized ? "#f6ffed" : "#f0f7ff"
	const borderColor = isFinalized ? "#b7eb8f" : "#cfe5f5"
	const footerBg = isFinalized ? "#f9fff6" : "#f8fbff"

	const treatedCount = box.orders.filter(
		(o) => treatedOrders[o._id] ?? o.treated
	).length
	const totalCount = box.orders.length

	const canFinalizeBox = box.registeredId && !isFinalized

	return (
		<Spin spinning={loading}>
			<div
				style={{
					...cardBase,
					border: `1px solid ${borderColor}`,
					borderTop: `3px solid ${accentColor}`,
				}}
			>
				{/* Header */}
				<div
					style={{
						padding: "10px 14px 9px",
						background: headerBg,
						borderBottom: `1px solid ${borderColor}`,
						display: "flex",
						alignItems: "flex-start",
						justifyContent: "space-between",
						gap: 8,
					}}
				>
					<div style={{ minWidth: 0 }}>
						<div
							style={{
								fontWeight: 700,
								fontSize: 14,
								color: "#1a1a2e",
								overflow: "hidden",
								textOverflow: "ellipsis",
								whiteSpace: "nowrap",
								lineHeight: 1.3,
							}}
						>
							{box.boxId || (
								<span
									style={{
										color: "#bbb",
										fontWeight: 400,
										fontStyle: "italic",
									}}
								>
									{Strings.orders.aggregationBoxNoId}
								</span>
							)}
						</div>
						{box.lot && (
							<div
								style={{
									fontSize: 11,
									color: "#999",
									marginTop: 2,
									lineHeight: 1,
								}}
							>
								{box.lot}
							</div>
						)}
					</div>

					<Tag
						color={isFinalized ? "success" : "processing"}
						icon={isFinalized ? <CheckCircleFilled /> : <ClockCircleOutlined />}
						style={{
							display: "flex",
							alignItems: "center",
							margin: 0,
							borderRadius: 4,
							fontSize: 10,
							lineHeight: "18px",
							padding: "0 6px",
							flexShrink: 0,
						}}
					>
						{isFinalized
							? Strings.orders.aggregationFinalized
							: Strings.orders.aggregationTemporary}
					</Tag>
				</div>

				{/* Orders */}
				<div style={{ padding: "10px 14px", flex: 1 }}>
					<div
						style={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
							marginBottom: 7,
						}}
					>
						<span style={{ fontSize: 11, color: "#aaa", fontWeight: 600 }}>
							{Strings.orders.aggregationOrdersLabel}
						</span>
						<span
							style={{
								fontSize: 11,
								fontWeight: 600,
								color: treatedCount === totalCount ? "#52c41a" : "#888",
							}}
						>
							{treatedCount}/{totalCount}
						</span>
					</div>

					<div
						style={{
							maxHeight: 200,
							overflowY: "auto",
							display: "flex",
							flexDirection: "column",
						}}
					>
						{box.orders.length === 0 ? (
							<div
								style={{
									fontSize: 12,
									color: "#ccc",
									textAlign: "center",
									padding: "8px 0",
								}}
							>
								{Strings.orders.aggregationNoOrders}
							</div>
						) : (
							box.orders.map((order) => {
								const isTreated = treatedOrders[order._id] ?? order.treated
								const isLoading = !!loadingOrders[order._id]
								const canToggle = box.registeredId && !isFinalized && !isLoading
								return (
									<div
										key={order._id}
										onClick={
											canToggle
												? () => onTreatedChange(order._id, !isTreated)
												: undefined
										}
										onMouseEnter={
											canToggle ? () => setHoveredOrder(order._id) : undefined
										}
										onMouseLeave={() => setHoveredOrder(null)}
										style={{
											display: "flex",
											alignItems: "center",
											justifyContent: "space-between",
											padding: "3px 4px",
											borderBottom: "1px solid #f5f5f5",
											borderRadius: 4,
											cursor: canToggle ? "pointer" : "default",
											background:
												hoveredOrder === order._id ? "#f0f7ff" : "transparent",
											transition: "background 0.15s",
										}}
									>
										<span
											style={{
												fontSize: 12,
												color: isTreated ? "#bbb" : "#444",
												overflow: "hidden",
												textOverflow: "ellipsis",
												whiteSpace: "nowrap",
												textDecoration: isTreated ? "line-through" : "none",
												transition: "color 0.2s",
											}}
										>
											<span style={{ fontWeight: 500 }}>{order.orderId}</span>
											<span style={{ color: "#ddd", margin: "0 4px" }}>·</span>
											<span>{order.bagId}</span>
										</span>
										{isLoading ? (
											<Spin
												size="small"
												style={{ flexShrink: 0, marginLeft: 4 }}
											/>
										) : (
											<Checkbox
												disabled={!box.registeredId || isFinalized}
												checked={isTreated}
												onClick={(e) => e.stopPropagation()}
												onChange={(e) =>
													onTreatedChange(order._id, e.target.checked)
												}
												style={{ flexShrink: 0, marginLeft: 4 }}
											/>
										)}
									</div>
								)
							})
						)}
					</div>
				</div>

				{/* Footer */}
				<div
					style={{
						padding: "9px 14px",
						borderTop: `1px solid ${borderColor}`,
						background: footerBg,
						display: "flex",
						flexDirection: "column",
						gap: 6,
					}}
				>
					<Input.Search
						size="small"
						placeholder={Strings.orders.aggregationEnterBoxId}
						enterButton={<CheckOutlined style={{ fontSize: 12 }} />}
						disabled={isFinalized}
						value={boxIdInput}
						onChange={(e) => onBoxIdChange(e.target.value)}
						onSearch={onBoxIdSubmit}
						onBlur={onBoxIdSubmit}
						style={{ fontSize: 12 }}
					/>

					{isPendingConfirm ? (
						<div
							style={{
								background: "#fff7e6",
								border: "1px solid #ffd591",
								borderRadius: 6,
								padding: "7px 10px",
								display: "flex",
								flexDirection: "column",
								gap: 6,
							}}
						>
							<div
								style={{
									fontSize: 11,
									color: "#d46b08",
									fontWeight: 600,
									display: "flex",
									alignItems: "center",
									gap: 5,
								}}
							>
								<WarningFilled style={{ fontSize: 12 }} />
								{
									box.orders.filter((o) => !(treatedOrders[o._id] ?? o.treated))
										.length
								}{" "}
								{Strings.orders.aggregationUntreatedWarning}
							</div>
							<div style={{ display: "flex", gap: 6 }}>
								<Button
									size="small"
									block
									onClick={onCancelFinalize}
									style={{ fontSize: 11, borderRadius: 5, flex: 1 }}
								>
									{Strings.orders.aggregationCancel}
								</Button>
								<Button
									size="small"
									block
									danger
									type="primary"
									onClick={onConfirmFinalize}
									style={{ fontSize: 11, borderRadius: 5, flex: 1 }}
								>
									{Strings.orders.aggregationFinalizeConfirm}
								</Button>
							</div>
						</div>
					) : (
						<Button
							type="primary"
							size="small"
							block
							onClick={onFinalize}
							disabled={!canFinalizeBox}
							style={{
								fontSize: 12,
								borderRadius: 6,
								fontWeight: 600,
								background: canFinalizeBox ? "#227cb5" : "#f5f5f5",
								borderColor: canFinalizeBox ? "#227cb5" : "#d9d9d9",
							}}
						>
							{Strings.orders.aggregationFinalizeBox}
						</Button>
					)}
				</div>
			</div>
		</Spin>
	)
}

// ─── OutOfFormatCard ──────────────────────────────────────────────────────────

interface OutOfFormatCardProps {
	item: any
	treatedOrders: Record<string, boolean>
	loadingOrders: Record<string, boolean>
	onTreatedChange: (orderId: string, checked: boolean) => void
	title?: string
}

const OutOfFormatCard: React.FC<OutOfFormatCardProps> = ({
	item,
	treatedOrders,
	loadingOrders,
	onTreatedChange,
	title = Strings.orders.outOfFormat,
}) => {
	const [hoveredOrder, setHoveredOrder] = React.useState<string | null>(null)
	const orders: any[] = item.orders ?? []
	const treatedCount = orders.filter(
		(o) => treatedOrders[o._id] ?? o.treated
	).length

	return (
		<Spin spinning={false}>
			<div
				style={{
					...cardBase,
					border: "1px solid #ffd591",
					borderTop: "3px solid #fa8c16",
				}}
			>
				{/* Header */}
				<div
					style={{
						padding: "10px 14px 9px",
						background: "#fffbe6",
						borderBottom: "1px solid #ffd591",
						display: "flex",
						alignItems: "flex-start",
						justifyContent: "space-between",
						gap: 8,
					}}
				>
					<div style={{ minWidth: 0 }}>
						<div
							style={{
								fontWeight: 700,
								fontSize: 14,
								color: "#1a1a2e",
								lineHeight: 1.3,
							}}
						>
							{title}
						</div>
						{item.lot && (
							<div
								style={{
									fontSize: 11,
									color: "#999",
									marginTop: 2,
									lineHeight: 1,
								}}
							>
								{item.lot}
							</div>
						)}
					</div>
					<WarningFilled style={{ color: "#fa8c16" }} />
				</div>

				{/* Orders */}
				<div style={{ padding: "10px 14px", flex: 1 }}>
					<div
						style={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
							marginBottom: 7,
						}}
					>
						<span style={{ fontSize: 11, color: "#aaa", fontWeight: 600 }}>
							ORDERS
						</span>
						<span
							style={{
								fontSize: 11,
								fontWeight: 600,
								color: treatedCount === orders.length ? "#52c41a" : "#888",
							}}
						>
							{treatedCount}/{orders.length}
						</span>
					</div>

					<div
						style={{
							maxHeight: 230,
							overflowY: "auto",
							display: "flex",
							flexDirection: "column",
						}}
					>
						{orders.map((order) => {
							const isTreated = treatedOrders[order._id] ?? order.treated
							const isLoading = !!loadingOrders[order._id]
							return (
								<div
									key={order._id}
									onClick={
										!isLoading
											? () => onTreatedChange(order._id, !isTreated)
											: undefined
									}
									onMouseEnter={
										!isLoading ? () => setHoveredOrder(order._id) : undefined
									}
									onMouseLeave={() => setHoveredOrder(null)}
									style={{
										display: "flex",
										alignItems: "center",
										justifyContent: "space-between",
										padding: "3px 4px",
										borderBottom: "1px solid #f5f5f5",
										borderRadius: 4,
										cursor: isLoading ? "default" : "pointer",
										background:
											hoveredOrder === order._id ? "#fff7e6" : "transparent",
										transition: "background 0.15s",
									}}
								>
									<span
										style={{
											fontSize: 12,
											color: isTreated ? "#bbb" : "#444",
											overflow: "hidden",
											textOverflow: "ellipsis",
											whiteSpace: "nowrap",
											textDecoration: isTreated ? "line-through" : "none",
											transition: "color 0.2s",
										}}
									>
										<span style={{ fontWeight: 500 }}>{order.orderId}</span>
										<span style={{ color: "#ddd", margin: "0 4px" }}>·</span>
										<span>{order.bagId || order.boxId}</span>
									</span>
									{isLoading ? (
										<Spin
											size="small"
											style={{ flexShrink: 0, marginLeft: 4 }}
										/>
									) : (
										<Checkbox
											checked={isTreated}
											onClick={(e) => e.stopPropagation()}
											onChange={(e) =>
												onTreatedChange(order._id, e.target.checked)
											}
											style={{ flexShrink: 0, marginLeft: 4 }}
										/>
									)}
								</div>
							)
						})}
					</div>
				</div>

				{/* Footer label */}
				<div
					style={{
						padding: "9px 14px",
						borderTop: "1px solid #ffd591",
						background: "#fffdf7",
					}}
				>
					<Input
						size="small"
						value={title}
						disabled
						style={{ fontSize: 12, color: "#fa8c16" }}
					/>
				</div>
			</div>
		</Spin>
	)
}

// ─── AggregationModal ─────────────────────────────────────────────────────────

export const AggregationModal: React.FC<{
	open: boolean
	onClose: () => void
	business?: string
	contract?: string
	date?: string
	showUntreatedBoxes?: boolean
	onFinalizeAggregation?: () => void
}> = ({
	open,
	onClose,
	business,
	contract,
	date,
	showUntreatedBoxes,
	onFinalizeAggregation,
}) => {
	const [boxes, setBoxes] = useState<AggregationBox[]>([])
	const [outOfFormat, setOutOfFormat] = useState<any[]>([])
	const [readyBoxes, setReadyBoxes] = useState<any[]>([])
	const [boxIdInputs, setBoxIdInputs] = useState<Record<string, string>>({})
	const [treatedOrders, setTreatedOrders] = useState<Record<string, boolean>>(
		{}
	)
	const [loadingOrders, setLoadingOrders] = useState<Record<string, boolean>>(
		{}
	)
	const [loadingBoxes, setLoadingBoxes] = useState<Record<string, boolean>>({})
	const [isLoadingData, setIsLoadingData] = useState(false)
	const [pendingFinalizeId, setPendingFinalizeId] = useState<string | null>(
		null
	)
	const [isFinalizing, setIsFinalizing] = useState(false)
	const [pendingFinalizeAggregation, setPendingFinalizeAggregation] =
		useState(false)

	useEffect(() => {
		if (!open) return

		setIsLoadingData(true)
		API.get({
			url: Endpoints.v2.uriOrders("aggregation"),
			params: { business, contract, date },
		})
			.then((response: any) => {
				const results = response?.data?.results || {}
				const data = results.boxes || []
				const outOfFormat = results.outOfFormat || []
				setBoxes(data)
				setOutOfFormat(outOfFormat)
				setReadyBoxes(results.readyBoxes || [])
				const inputs: Record<string, string> = {}
				data.forEach((box: AggregationBox) => {
					inputs[box._id] = box.registeredId ? box.boxId : ""
				})
				setBoxIdInputs(inputs)
			})
			.catch((error: any) => {
				console.error("Failed to fetch aggregation:", error)
			})
			.finally(() => {
				setIsLoadingData(false)
			})
	}, [open, business, contract, date])

	const handleBoxIdChange = (boxKey: string, value: string) => {
		setBoxIdInputs((prev) => ({ ...prev, [boxKey]: value }))
	}

	const handleBoxIdSubmit = (box: AggregationBox) => {
		const newBoxId = boxIdInputs[box._id]
		if (!newBoxId || newBoxId === box.boxId) return

		const isDuplicate = boxes.some(
			(b) => b._id !== box._id && b.boxId === newBoxId
		)
		if (isDuplicate) {
			notification.error({
				message: Strings.orders.aggregation,
				description: Strings.orders.duplicateBoxId,
			})
			return
		}

		setLoadingBoxes((prev) => ({ ...prev, [box._id]: true }))

		API.patch({
			url: Endpoints.v2.uriOrders("aggregation/box-id"),
			data: {
				boxId: newBoxId,
				orders: box.orders.map((o) => o._id),
			},
		})
			.then((response: any) => {
				if (!response?.ok) {
					const errorMsg = response?.data?.message || "Internal error"
					notification.error({
						message: Strings.orders.aggregation,
						description: errorMsg,
					})
					return
				}
				setBoxes((prev) =>
					prev.map((b) =>
						b._id === box._id
							? { ...b, boxId: newBoxId, registeredId: true }
							: b
					)
				)
			})
			.finally(() => {
				setLoadingBoxes((prev) => ({ ...prev, [box._id]: false }))
			})
	}

	const doFinalizeBox = (box: AggregationBox) => {
		setLoadingBoxes((prev) => ({ ...prev, [box._id]: true }))

		API.patch({
			url: Endpoints.v2.uriOrders("aggregation/box-finalization"),
			data: {
				boxId: box.boxId,
				orders: box.orders.map((o) => o._id),
			},
		})
			.then((response: any) => {
				if (!response?.ok) {
					const errorMsg = response?.data?.message || "Internal error"
					notification.error({
						message: Strings.orders.aggregation,
						description: errorMsg,
					})
					return
				}
				setBoxes((prev) =>
					prev.map((b) => (b._id === box._id ? { ...b, temporary: false } : b))
				)
			})
			.finally(() => {
				setLoadingBoxes((prev) => ({ ...prev, [box._id]: false }))
			})
	}

	const handleFinalizeBox = (box: AggregationBox) => {
		if (!showUntreatedBoxes) {
			const untreatedCount = box.orders.filter(
				(o) => !(treatedOrders[o._id] ?? o.treated)
			).length

			if (untreatedCount > 0) {
				setPendingFinalizeId(box._id)
				return
			}
		}

		doFinalizeBox(box)
	}

	const handleConfirmFinalize = (box: AggregationBox) => {
		setPendingFinalizeId(null)
		doFinalizeBox(box)
	}

	const handleCancelFinalize = () => {
		setPendingFinalizeId(null)
	}

	const doFinalizeAggregation = () => {
		const allOrderIds = [
			...boxes.flatMap((box) => box.orders.map((o) => o._id)),
			...[...outOfFormat, ...readyBoxes].flatMap((item) =>
				(item.orders ?? []).map((o: any) => o._id)
			),
		]
		if (!allOrderIds.length) return

		setPendingFinalizeAggregation(false)
		setIsFinalizing(true)

		API.patch({
			url: Endpoints.uriOrders("status"),
			data: {
				status: "packing",
				orders: allOrderIds,
				aggregationEnded: true,
			},
			headers: { business, contract },
			// Closing a big lot (load sequences, PDFs, Route4me) can outlast the
			// default 30s; a timeout here showed an error for a close that succeeded
			axiosConfig: { timeout: 120000 },
		})
			.then((response: any) => {
				if (!response?.ok) {
					const errorMsg = response?.data?.message || "Internal error"
					const missingOrders = response?.data?.results?.orders
					Modal.error({
						title: Strings.orders.aggregation,
						content: missingOrders?.length ? (
							<div>
								<p>{errorMsg}</p>
								<ul
									style={{ paddingLeft: 20, maxHeight: 200, overflowY: "auto" }}
								>
									{missingOrders.map((o: any, i: number) => (
										<li key={i}>
											{o.dingoo_order_id || o.order_id || o.optimizer_order_id}
										</li>
									))}
								</ul>
							</div>
						) : (
							errorMsg
						),
					})
					return
				}
				notification.success({
					message: Strings.orders.aggregation,
					description: Strings.orders.aggregationFinalizeAll,
				})
				onFinalizeAggregation?.()
				onClose()
			})
			.finally(() => {
				setIsFinalizing(false)
			})
	}

	const handleFinalizeAggregation = () => {
		const untreatedOutOfFormat = [...outOfFormat, ...readyBoxes]
			.flatMap((item) => item.orders ?? [])
			.filter((o: any) => !(treatedOrders[o._id] ?? o.treated)).length

		if (untreatedOutOfFormat > 0) {
			setPendingFinalizeAggregation(true)
			return
		}

		doFinalizeAggregation()
	}
	const handleTreatedChange = (orderId: string, checked: boolean) => {
		setLoadingOrders((prev) => ({ ...prev, [orderId]: true }))

		API.patch({
			url: Endpoints.v2.uriOrders("aggregation/bag-treated"),
			data: { orders: [orderId], treated: checked },
		})
			.then((response: any) => {
				if (!response?.ok) {
					console.log("Response error:", response)
					const errorMsg = response?.data?.message || "Internal error"
					notification.error({
						message: Strings.orders.aggregation,
						description: errorMsg,
					})
					return
				}
				setTreatedOrders((prev) => ({ ...prev, [orderId]: checked }))
			})
			.finally(() => {
				setLoadingOrders((prev) => ({ ...prev, [orderId]: false }))
			})
	}

	const finalizedCount = boxes.filter((b) => !b.temporary).length

	const modalTitle = (
		<div style={{ display: "flex", alignItems: "center", gap: 10 }}>
			<span style={{ fontWeight: 700, fontSize: 16 }}>
				{Strings.orders.aggregation}
			</span>
			{!isLoadingData && (
				<div style={{ display: "flex", gap: 6 }}>
					{boxes.length > 0 && (
						<Tag
							color="blue"
							style={{ margin: 0, borderRadius: 4, fontSize: 11 }}
						>
							{boxes.length}{" "}
							{boxes.length === 1
								? Strings.orders.aggregationBoxSingular
								: Strings.orders.aggregationBoxPlural}
						</Tag>
					)}
					{finalizedCount > 0 && (
						<Tag
							color="success"
							style={{ margin: 0, borderRadius: 4, fontSize: 11 }}
						>
							{finalizedCount} {Strings.orders.aggregationFinalizedLabel}
						</Tag>
					)}
					{outOfFormat.length > 0 && (
						<Tag
							color="warning"
							style={{ margin: 0, borderRadius: 4, fontSize: 11 }}
						>
							{outOfFormat.length} {Strings.orders.outOfFormat}
						</Tag>
					)}
					{readyBoxes.length > 0 && (
						<Tag
							color="default"
							style={{ margin: 0, borderRadius: 4, fontSize: 11 }}
						>
							{readyBoxes.flatMap((item) => item.orders ?? []).length}{" "}
							{Strings.orders.readyBoxes}
						</Tag>
					)}
				</div>
			)}
		</div>
	)

	const allBoxesFinalized = boxes.every((b) => !b.temporary)
	const allOrderIds = [
		...boxes.flatMap((box) => box.orders.map((o) => o._id)),
		...[...outOfFormat, ...readyBoxes].flatMap((item) =>
			(item.orders ?? []).map((o: any) => o._id)
		),
	]
	const canFinalizeAggregation = allBoxesFinalized && allOrderIds.length > 0

	const untreatedOutOfFormatCount = [...outOfFormat, ...readyBoxes]
		.flatMap((item) => item.orders ?? [])
		.filter((o: any) => !(treatedOrders[o._id] ?? o.treated)).length

	const modalFooter = pendingFinalizeAggregation ? (
		<div
			style={{
				background: "#fff7e6",
				border: "1px solid #ffd591",
				borderRadius: 6,
				padding: "7px 10px",
				display: "flex",
				alignItems: "center",
				justifyContent: "space-between",
				gap: 10,
			}}
		>
			<div
				style={{
					fontSize: 12,
					color: "#d46b08",
					fontWeight: 600,
					display: "flex",
					alignItems: "center",
					gap: 5,
				}}
			>
				<WarningFilled style={{ fontSize: 13 }} />
				{untreatedOutOfFormatCount} {Strings.orders.aggregationUntreatedWarning}
			</div>
			<div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
				<Button
					size="small"
					onClick={() => setPendingFinalizeAggregation(false)}
					style={{ fontSize: 11, borderRadius: 5 }}
				>
					{Strings.orders.aggregationCancel}
				</Button>
				<Button
					size="small"
					danger
					type="primary"
					onClick={doFinalizeAggregation}
					style={{ fontSize: 11, borderRadius: 5 }}
				>
					{Strings.orders.aggregationFinalizeConfirm}
				</Button>
			</div>
		</div>
	) : (
		<Button
			type="primary"
			loading={isFinalizing}
			disabled={!canFinalizeAggregation || isLoadingData}
			onClick={handleFinalizeAggregation}
			style={{
				background: canFinalizeAggregation ? "#52c41a" : undefined,
				borderColor: canFinalizeAggregation ? "#52c41a" : undefined,
				fontWeight: 600,
			}}
		>
			{Strings.orders.aggregationFinalizeAll}
		</Button>
	)

	return (
		<Modal
			title={modalTitle}
			visible={open}
			onCancel={onClose}
			footer={modalFooter}
			width="70vw"
			bodyStyle={{
				padding: "20px 24px",
				maxHeight: "75vh",
				overflowY: "auto",
			}}
		>
			{isLoadingData ? (
				<div style={{ textAlign: "center", padding: "64px 0", color: "#bbb" }}>
					<Spin size="large" />
				</div>
			) : (
				<div
					style={{
						display: "grid",
						gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
						gap: 14,
					}}
				>
					{boxes.map((box) => (
						<BoxCard
							key={box._id}
							box={box}
							loading={!!loadingBoxes[box._id]}
							treatedOrders={treatedOrders}
							loadingOrders={loadingOrders}
							boxIdInput={boxIdInputs[box._id] ?? ""}
							onBoxIdChange={(val) => handleBoxIdChange(box._id, val)}
							onBoxIdSubmit={() => handleBoxIdSubmit(box)}
							onTreatedChange={handleTreatedChange}
							onFinalize={() => handleFinalizeBox(box)}
							isPendingConfirm={pendingFinalizeId === box._id}
							onConfirmFinalize={() => handleConfirmFinalize(box)}
							onCancelFinalize={handleCancelFinalize}
						/>
					))}
					{outOfFormat.map((item, index) => (
						<OutOfFormatCard
							key={index}
							item={item}
							treatedOrders={treatedOrders}
							loadingOrders={loadingOrders}
							onTreatedChange={handleTreatedChange}
						/>
					))}
					{readyBoxes.map((item, index) => (
						<OutOfFormatCard
							key={`ready-${index}`}
							item={item}
							title={Strings.orders.readyBoxes}
							treatedOrders={treatedOrders}
							loadingOrders={loadingOrders}
							onTreatedChange={handleTreatedChange}
						/>
					))}
				</div>
			)}
		</Modal>
	)
}
